import { useMutation, useQueryClient } from '@tanstack/react-query';

import { pastExportsApi } from '@/features/data-hub/api/pastExports.api';
import { pastExportsOptions } from '@/features/data-hub/api/pastExports.options';
import type { ExportItemType } from '@/features/data-hub/types/pastExports';
import useSnackbarNotifications from '@/shared/hooks/useSnackbarNotifications';

const snackbarKey = 'deleteExport';

export const useDeletePastExport = () => {
  const queryClient = useQueryClient();
  const { showErrorSnackbar, showSuccessSnackbar, showLoadingSnackbar } =
    useSnackbarNotifications();

  return useMutation({
    mutationFn: (id: string) => pastExportsApi.deletePastExport(id),
    mutationKey: [...pastExportsOptions.all(), 'delete'] as const,
    onMutate: () => {
      showLoadingSnackbar('Deleting export...', snackbarKey);
    },
    onSuccess: (_response, id) => {
      showSuccessSnackbar('Past export has been deleted', snackbarKey);
      queryClient.removeQueries({ queryKey: pastExportsOptions.detail(id) });
      queryClient.setQueriesData<ExportItemType[]>({ queryKey: pastExportsOptions.lists() }, old =>
        old?.filter(item => item.id !== id)
      );
      // Delay refetch — backend still returns deleted item briefly
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: pastExportsOptions.lists() });
      }, 3000);
    },
    onError: () => {
      showErrorSnackbar('Error deleting past export', 2000, snackbarKey);
    }
  });
};
